import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { User } from '../models/user.model';
import { LoggerService } from '../services/logger.service';
import { GlobalModalService } from '../shared/global-modal/global-modal.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private permissionsSubject = new BehaviorSubject<any>({});
  public permissions$ = this.permissionsSubject.asObservable();

  constructor(private http: HttpClient, private router: Router, private logger: LoggerService, private modalService: GlobalModalService) {}

  login(username: string, password: string, opts?: { skipErrorModal?: boolean }): Observable<any> {
    this.logger.log("AuthService.login.");
    const headers: any = {};
    if (opts?.skipErrorModal) headers['x-skip-error-modal'] = 'true';
    return this.http.post<any>(environment.api_service_auth+'/login', { username, password }, { headers }).pipe(
      tap((response) => {
        localStorage.setItem('token', response.token);
        localStorage.setItem('refreshToken', response.refreshToken);

        const user = new User(
            response.user.id, response.user.username, '', response.user.email, response.user.createdat, 
            response.user.consumedtokens, response.user.totaltokens, response.user.tenantid, response.user.issuperadmin
        );
        localStorage.setItem('currentUser', JSON.stringify(user));
      })
    );
  }

  requestAccess(nombre: string, email: string, password: string): Observable<any> {
    this.logger.log("AuthService.requestAccess.");
    return this.http.post(environment.api_service_auth + '/request-access', { username: nombre, email, password });
  }

  isSuperAdmin(): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    return user.issuperadmin === true || String(user.issuperadmin).toLowerCase() === 'true';
  }

  getActiveTenantId(): string | null { return localStorage.getItem('activeTenantId'); }

  setActiveTenantId(tenantId: string): void {
    if (tenantId) localStorage.setItem('activeTenantId', tenantId);
    else localStorage.removeItem('activeTenantId');
  }

  register(username: string, password: string): Observable<Object> {
    return this.http.post(environment.api_service_auth_registeradmin, { username, password });
  }

  loginWithGoogle(): Observable<Object> { return this.http.get(environment.api_service_auth_google); }
  isLoggedIn(): boolean { return !!localStorage.getItem('token'); }
  
  refreshToken(): Observable<any> {
    const refreshToken = localStorage.getItem('refreshToken');
    return this.http.post(environment.api_service_refresh_token, { refreshToken });
  }

  logout(mostrarMensajeExpulsado: boolean = false) {
    localStorage.removeItem('token'); localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser'); localStorage.removeItem('activeTenantId'); 
    sessionStorage.clear(); 

    if (mostrarMensajeExpulsado) {
      this.modalService.showError('Sesión Expirada', 'Tu sesión ha expirado por inactividad o tus permisos han sido modificados. Por favor, vuelve a ingresar.');
    }
    this.router.navigate(['/login']);
  }

  loadMyPermissions(): Observable<any> {
    return this.http.get(environment.api_service_permissions).pipe(
      tap(permisos => { this.permissionsSubject.next(permisos); })
    );
  }

  hasPermission(permisoBuscado: string, contextoEspecifico?: string): boolean {
    if (this.isSuperAdmin()) return true;
    const permisos = this.permissionsSubject.value;
    if (!permisos || Object.keys(permisos).length === 0) return false;

    if (contextoEspecifico) {
      if (permisos[contextoEspecifico]) {
        const contextPerms = Array.isArray(permisos[contextoEspecifico]) ? permisos[contextoEspecifico] : [permisos[contextoEspecifico]];
        if (contextPerms.includes(permisoBuscado)) return true;
      }
      return false; 
    }

    for (const contexto in permisos) {
      const contextPerms = Array.isArray(permisos[contexto]) ? permisos[contexto] : [permisos[contexto]];
      if (contextPerms.includes(permisoBuscado)) return true;
    }
    return false;
  }

  setAccessToken(token: string): void { localStorage.setItem('token', token); }
  setRefreshToken(refreshToken: string): void { localStorage.setItem('refreshToken', refreshToken); }
  getAccessToken(): string | null { return localStorage.getItem('token'); }

  setCurrentUser(usuario: User): void { localStorage.setItem('currentUser', JSON.stringify(usuario)); }

  getCurrentUser(): User | null {
    const userJson = localStorage.getItem('currentUser');
    if (userJson) {
      const userObj = JSON.parse(userJson);
      return new User(userObj.id, userObj.username, '', userObj.email, userObj.createdAt, userObj.consumedtokens, userObj.totaltokens, userObj.tenantid, userObj.issuperadmin);
    }
    return null;
  }

  // Se mantiene solo la LECTURA de peticiones en Auth (por ser de acceso previo)
  getPendingRequests(): Observable<any[]> {
    return this.http.get<any[]>(environment.api_service_auth + '/requests');
  }
}